import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ValidatorComponent } from '@feature/survey-management/components/validator/validator.component';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { ValidatorType } from '@shared/models/enums/validator-type.enum';
import { SingleSelect } from '@shared/models/question-types/single-select';

@Component({
  selector: 'app-single-select-dropdown-input',
  imports: [
    NgSelectComponent,
    NgOptionComponent,
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ValidatorComponent,
  ],
  templateUrl: './single-select-dropdown-input.component.html',
  styleUrl: './single-select-dropdown-input.component.scss',
})
export class SingleSelectDropdownInputComponent implements OnDestroy {
  @Input({ required: true }) question!: SingleSelect;
  @Input() control?: any;
  @Input() otherControl?: any;
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() inTable: boolean = false;
  @Input() hideError = false;
  @ViewChild('otherInput') otherInput?: ElementRef<HTMLInputElement>;
  @ViewChild(NgSelectComponent) ngSelect?: NgSelectComponent;
  ValidatorType = ValidatorType;
  isDropdownOpen = false;
  private scrollListener?: (event: Event) => void;

  constructor(private elementRef: ElementRef) {}

  getAppendToElement(): string {
    return 'body';
  }

  getSelectedTitle(): string {
    if (!this.control?.value) {
      return this.placeholder ?? '';
    }

    return (
      this.question?.alternatives?.find(
        (alternative) => alternative.id === this.control.value,
      )?.title ?? ''
    );
  }

  isOtherSelected(): boolean {
    if (!this.question?.alternatives || !this.control) {
      return false;
    }
    const selectedValue = this.control.value;
    const selectedOption = this.question.alternatives.find(
      (alternative) => alternative.id === selectedValue,
    );
    return !!(selectedOption && selectedOption.type === 'OTHER');
  }

  handleOnChange(value: string) {
    if (!value) {
      this.control.reset();
    }
    if (this.isOtherSelected()) {
      this.otherControl.enable();
      // Focus the other input after it's rendered
      setTimeout(() => {
        this.otherInput?.nativeElement.focus();
      }, 0);
    } else {
      this.otherControl?.disable();
    }
  }

  onOpen(): void {
    this.isDropdownOpen = true;
    if (this.inTable) {
      this.attachScrollListener();
      const cell = this.elementRef.nativeElement.closest('td');
      setTimeout(() => {
        if (
          cell &&
          typeof (cell as HTMLElement).scrollIntoView === 'function'
        ) {
          (cell as HTMLElement).scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'end',
          });
          this.ngSelect?.open();
        }
      }, 100);
    }
  }

  onClose(): void {
    this.isDropdownOpen = false;
    if (this.inTable) {
      this.removeScrollListener();
    }
  }

  private attachScrollListener(): void {
    if (this.scrollListener) return;

    this.scrollListener = (event?: Event) => {
      // Do not close if scroll is from dropdown panel items
      if (event && event.target instanceof HTMLElement) {
        if (
          event.target.classList.contains('ng-dropdown-panel-items') &&
          event.target.classList.contains('scroll-host')
        ) {
          return;
        }
      }
      if (this.isDropdownOpen && this.ngSelect) {
        this.ngSelect.close();
      }
    };

    const formElement = document.getElementById('survey-form');
    if (formElement) {
      formElement.addEventListener('scroll', this.scrollListener, true);
    }
    // Also listen to window scroll
    window.addEventListener('scroll', this.scrollListener, true);
  }

  private removeScrollListener(): void {
    if (!this.scrollListener) return;

    const formElement = document.getElementById('survey-form');
    if (formElement) {
      formElement.removeEventListener('scroll', this.scrollListener, true);
    }
    window.removeEventListener('scroll', this.scrollListener, true);
    this.scrollListener = undefined;
  }

  ngOnDestroy(): void {
    this.removeScrollListener();
  }
}
